import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, List, ListItem } from '@mui/material';
import { useDraggable } from '@dnd-kit/core';
import {
  TextIcon as LineIcon,
  Barcode,
  ALargeSmallIcon as TextIcon,
  Square,
  ImageIcon,
  QrCode,
  Table,
} from 'lucide-react';
import { ElementType } from '../../types';
import { colorPalette } from 'ui/theme';

const DRAWER_WIDTH = 280;

interface DraggableComponentProps {
  id: ElementType;
  icon: React.ReactNode;
  label: string;
}

const DraggableComponent = ({ id, icon, label }: DraggableComponentProps) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
  });

  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        requestAnimationFrame(() => {
          setPosition({ x: e.clientX, y: e.clientY });
        });
      }
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove, { passive: true });
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDragging]);

  // Create a portal for the drag preview to ensure it's not constrained by parent containers
  useEffect(() => {
    // Make sure the drag preview container exists
    let dragPreviewContainer = document.getElementById(
      'drag-preview-container'
    );
    if (!dragPreviewContainer) {
      dragPreviewContainer = document.createElement('div');
      dragPreviewContainer.id = 'drag-preview-container';
      dragPreviewContainer.style.position = 'fixed';
      dragPreviewContainer.style.top = '0';
      dragPreviewContainer.style.left = '0';
      dragPreviewContainer.style.width = '0';
      dragPreviewContainer.style.height = '0';
      dragPreviewContainer.style.zIndex = '100000';
      dragPreviewContainer.style.pointerEvents = 'none';
      document.body.appendChild(dragPreviewContainer);
    }
  }, []);

  return (
    <ListItem
      ref={setNodeRef}
      sx={{
        padding: 1,
        transition: 'all 0.2s',
        cursor: 'grab',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          borderRadius: 1,
        },
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
      }}
      {...attributes}
      {...listeners}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          padding: 1,
          border: '1px dashed',
          borderColor: 'grey.300',
          borderRadius: 1,
          '&:hover': {
            borderColor: colorPalette.redesign.highlightBlue,
          },
        }}
      >
        <Box
          sx={{
            color: colorPalette.redesign.lightPrimaryBlue,
            fontSize: 32,
            display: 'flex',
            justifyContent: 'center',
            mb: 1,
          }}
        >
          {icon}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>

      {/* Drag Preview */}
      {isDragging &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              pointerEvents: 'none',
              zIndex: 100000,
              backgroundColor: colorPalette.redesign.lightPrimaryBlue,
              color: 'white',
              padding: '8px 16px',
              borderRadius: '4px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              left: 0,
              top: 0,
              transform: `translate(${position.x}px, ${position.y}px)`,
              willChange: 'transform',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.1)',
              opacity: 1,
            }}
          >
            {icon}
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {label}
            </Typography>
          </div>,
          document.getElementById('drag-preview-container') || document.body
        )}
    </ListItem>
  );
};

const ComponentSidebar = () => {
  const components: {
    id: ElementType;
    icon: React.ReactNode;
    label: string;
  }[] = [
    { id: 'text', icon: <TextIcon size={24} />, label: 'Text' },
    { id: 'barcode', icon: <Barcode size={24} />, label: 'Barcode' },
    { id: 'qrcode', icon: <QrCode size={24} />, label: 'QR Code' },
    { id: 'line', icon: <LineIcon size={24} />, label: 'Line' },
    { id: 'box', icon: <Square size={24} />, label: 'Box' },
    { id: 'image', icon: <ImageIcon size={24} />, label: 'Image' },
    { id: 'table', icon: <Table size={24} />, label: 'Table' },
    {
      id: 'advancedTable',
      icon: <Table size={24} />,
      label: 'Sales Item Table',
    },
  ];

  return (
    <Box
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        height: '100%',
        overflow: 'auto',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Components
        </Typography>
      </Box>
      <Box sx={{ mt: 2, px: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Drag items to the canvas
        </Typography>
      </Box>
      <List sx={{ padding: 2 }}>
        {components.map((component) => (
          <DraggableComponent
            key={component.id}
            id={component.id}
            icon={component.icon}
            label={component.label}
          />
        ))}
      </List>
    </Box>
  );
};

export default ComponentSidebar;
